import { motion } from "framer-motion";

const draw = (delay = 0) => ({
  initial: { pathLength: 0, opacity: 0 },
  whileInView: { pathLength: 1, opacity: 1 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 1.6, delay, ease: [0.22, 1, 0.36, 1] },
});

const Node = ({ cx, cy, delay = 0, color = "#6FA82C" }) => (
  <g>
    <motion.circle
      cx={cx} cy={cy} r={3.5} fill={color}
      initial={{ scale: 0, opacity: 0 }}
      whileInView={{ scale: 1, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ delay: delay + 1.1, duration: 0.4 }}
      style={{ transformOrigin: `${cx}px ${cy}px`, transformBox: "fill-box" }}
    />
    <circle cx={cx} cy={cy} r={9} fill="none" stroke={color} strokeOpacity={0.35} className="animate-pulse-dot" style={{ animationDelay: `${delay}s` }} />
  </g>
);

// Line-art commercial building wrapped in a device mesh — signature brand vector.
export const BuildingMesh = ({ className = "" }) => (
  <svg viewBox="0 0 520 420" fill="none" className={className} aria-hidden="true">
    <motion.path d="M60 400 V150 L190 90 V400" stroke="#BDC3C7" strokeWidth="1.4" {...draw(0)} />
    <motion.path d="M190 400 V60 L340 130 V400" stroke="#7F8C8D" strokeWidth="1.4" {...draw(0.15)} />
    <motion.path d="M340 400 V190 L460 240 V400" stroke="#BDC3C7" strokeWidth="1.4" {...draw(0.3)} />
    <motion.path d="M20 400 H500" stroke="#BDC3C7" strokeWidth="1.4" {...draw(0.4)} />
    {[0, 1, 2, 3].map((r) =>
      [0, 1, 2].map((c) => (
        <motion.rect
          key={`a${r}${c}`} x={85 + c * 34} y={170 + r * 50} width={16} height={24}
          stroke="#7F8C8D" strokeWidth="1" fill="none" {...draw(0.5 + r * 0.08)}
        />
      ))
    )}
    {[0, 1, 2, 3].map((r) =>
      [0, 1, 2].map((c) => (
        <motion.rect
          key={`b${r}${c}`} x={215 + c * 40} y={90 + r * 66} width={20} height={30}
          stroke="#7F8C8D" strokeWidth="1" fill="none" {...draw(0.6 + r * 0.08)}
        />
      ))
    )}
    {[0, 1].map((r) =>
      [0, 1].map((c) => (
        <motion.rect
          key={`c${r}${c}`} x={365 + c * 44} y={255 + r * 60} width={22} height={28}
          stroke="#7F8C8D" strokeWidth="1" fill="none" {...draw(0.7 + r * 0.1)}
        />
      ))
    )}
    <motion.path d="M102 182 L232 106 M232 106 L382 268 M102 182 L382 268 M146 232 L256 156 M256 156 L406 312 M146 232 L300 330" stroke="#101010" strokeOpacity={0.55} strokeWidth="1" strokeDasharray="4 5" {...draw(0.9)} />
    <motion.path d="M232 106 L300 330 M102 182 L300 330" stroke="#6FA82C" strokeOpacity={0.7} strokeWidth="1" strokeDasharray="4 5" {...draw(1.0)} />
    <Node cx={102} cy={182} delay={0.1} />
    <Node cx={232} cy={106} delay={0.25} />
    <Node cx={382} cy={268} delay={0.4} color="#101010" />
    <Node cx={146} cy={232} delay={0.55} />
    <Node cx={256} cy={156} delay={0.7} color="#101010" />
    <Node cx={406} cy={312} delay={0.85} />
    <Node cx={300} cy={330} delay={1.0} />
    <motion.circle cx={232} cy={106} r={18} stroke="#6FA82C" strokeOpacity={0.5} fill="none" {...draw(1.2)} />
  </svg>
);

// Concentric mesh rings — Technology page hero.
export const MeshRings = ({ className = "" }) => (
  <svg viewBox="0 0 400 400" fill="none" className={className} aria-hidden="true">
    {[60, 110, 160].map((r, i) => (
      <motion.circle key={r} cx={200} cy={200} r={r} stroke={i === 1 ? "#6FA82C" : "#BDC3C7"} strokeOpacity={i === 1 ? 0.6 : 1} strokeWidth="1" strokeDasharray={i === 2 ? "3 6" : undefined} {...draw(i * 0.2)} />
    ))}
    <motion.path d="M200 140 L290 252 L110 252 Z M200 90 V310 M90 200 H310 M200 140 L200 60 M290 252 L340 200 M110 252 L60 200" stroke="#7F8C8D" strokeWidth="0.8" strokeDasharray="3 4" {...draw(0.5)} />
    <Node cx={200} cy={140} delay={0.1} />
    <Node cx={290} cy={252} delay={0.25} color="#101010" />
    <Node cx={110} cy={252} delay={0.4} />
    <Node cx={200} cy={90} delay={0.55} color="#101010" />
    <Node cx={340} cy={200} delay={0.7} />
    <Node cx={60} cy={200} delay={0.85} color="#101010" />
    <Node cx={200} cy={310} delay={1.0} />
    <motion.circle cx={200} cy={200} r={7} fill="#6FA82C" initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 1.3 }} style={{ transformOrigin: "200px 200px", transformBox: "fill-box" }} />
    <circle cx={200} cy={200} r={16} fill="none" stroke="#6FA82C" strokeOpacity={0.4} className="animate-pulse-dot" />
  </svg>
);

// Horizontal circuit traces — section dividers and page heroes.
export const CircuitTraces = ({ className = "", light = false }) => {
  const base = light ? "#D4D4D8" : "#BDC3C7";
  return (
    <svg viewBox="0 0 1200 90" fill="none" preserveAspectRatio="none" className={className} aria-hidden="true">
      <motion.path d="M0 45 H420 L450 20 H780 L810 45 H1200" stroke={base} strokeWidth="1" {...draw(0)} />
      <motion.path d="M0 70 H260 L285 48 H540" stroke={base} strokeWidth="1" strokeOpacity={0.7} {...draw(0.2)} />
      <motion.path d="M1200 70 H960 L935 48 H700" stroke={base} strokeWidth="1" strokeOpacity={0.7} {...draw(0.3)} />
      <motion.path d="M0 20 H180 L205 42 H360" stroke="#6FA82C" strokeWidth="1.2" strokeOpacity={0.8} {...draw(0.4)} />
      <circle cx={360} cy={42} r={4} fill="#6FA82C" className="animate-pulse-dot" />
      <circle cx={450} cy={20} r={3.5} fill={base} />
      <circle cx={810} cy={45} r={3.5} fill={base} />
      <circle cx={285} cy={48} r={3} fill={base} />
      <circle cx={935} cy={48} r={3} fill={base} />
    </svg>
  );
};

// Interactive scene: tap ON, slider dimming, then daylight harvesting. 9s loop.
export const SmartControl = ({ className = "" }) => {
  const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const D = "9s";
  // phases: off | tap+on (full) | dim 50% | daylight harvest (auto) | reset
  const gT = "0;0.16;0.22;0.40;0.50;0.64;0.78;0.86;0.94;1";
  const gV = "0;0;1;1;0.45;0.45;0.08;0.08;0;0";
  const sunT = "0;0.64;0.74;0.86;0.93;1";
  const sunV = "0;0;1;1;0;0";
  const knobT = "0;0.12;0.2;0.40;0.50;0.64;0.78;0.88;0.95;1";
  const knobV = "235;235;261;261;247;247;243;243;235;235";
  const trackT = gT;
  const trackV = "0;0;1;1;0.55;0.55;0.25;0.25;0;0";
  return (
    <svg viewBox="0 0 300 360" fill="none" className={className} aria-hidden="true">
      {/* window */}
      <rect x={38} y={28} width={156} height={210} fill="#F7F8F4" stroke="#7F8C8D" strokeWidth={1.4} />
      <line x1={116} y1={28} x2={116} y2={238} stroke="#BDC3C7" strokeWidth={1} />
      <line x1={38} y1={133} x2={194} y2={133} stroke="#BDC3C7" strokeWidth={1} />
      <rect x={30} y={238} width={172} height={8} fill="#EEF1F4" stroke="#7F8C8D" strokeWidth={1} />
      {/* room brightening follows bulb */}
      <rect x={40} y={30} width={152} height={206} fill="#EAF3DC" opacity={0}>
        {!reduced && <animate attributeName="opacity" values={gV} keyTimes={gT} dur={D} repeatCount="indefinite" />}
      </rect>
      {/* sun — daylight harvesting phase */}
      <g opacity={0}>
        {!reduced && <animate attributeName="opacity" values={sunV} keyTimes={sunT} dur={D} repeatCount="indefinite" />}
        {!reduced && (
          <animateTransform attributeName="transform" type="translate" values="0 16;0 16;0 0;0 0;0 16;0 16" keyTimes={sunT} dur={D} repeatCount="indefinite" />
        )}
        <circle cx={164} cy={66} r={11} fill="#6FA82C" />
        <g stroke="#6FA82C" strokeWidth={1.6} strokeLinecap="round">
          <line x1={164} y1={48} x2={164} y2={42} />
          <line x1={164} y1={84} x2={164} y2={90} />
          <line x1={146} y1={66} x2={140} y2={66} />
          <line x1={182} y1={66} x2={188} y2={66} />
          <line x1={151} y1={53} x2={147} y2={49} />
          <line x1={177} y1={53} x2={181} y2={49} />
          <line x1={151} y1={79} x2={147} y2={83} />
          <line x1={177} y1={79} x2={181} y2={83} />
        </g>
      </g>
      {/* cord + bulb */}
      <line x1={116} y1={28} x2={116} y2={96} stroke="#101010" strokeWidth={1.4} />
      <rect x={110} y={96} width={12} height={9} fill="#101010" />
      <circle cx={116} cy={120} r={17} fill="#FFFFFF" stroke="#101010" strokeWidth={1.4} />
      <circle cx={116} cy={120} r={17} fill="#B7D978" opacity={0}>
        {!reduced && <animate attributeName="opacity" values={gV} keyTimes={gT} dur={D} repeatCount="indefinite" />}
      </circle>
      <path d="M110 116 q3 8 6 8 q3 0 6 -8" stroke="#101010" strokeWidth={1.2} fill="none" />
      <circle cx={116} cy={120} r={34} fill="#6FA82C" opacity={0}>
        {!reduced && <animate attributeName="opacity" values="0;0;0.22;0.22;0.1;0.1;0.02;0.02;0;0" keyTimes={gT} dur={D} repeatCount="indefinite" />}
      </circle>
      {/* rays */}
      <g stroke="#6FA82C" strokeWidth={1.6} strokeLinecap="round" opacity={0}>
        {!reduced && <animate attributeName="opacity" values={gV} keyTimes={gT} dur={D} repeatCount="indefinite" />}
        <line x1={86} y1={104} x2={78} y2={98} />
        <line x1={84} y1={120} x2={74} y2={120} />
        <line x1={86} y1={136} x2={78} y2={142} />
        <line x1={146} y1={104} x2={154} y2={98} />
        <line x1={148} y1={120} x2={158} y2={120} />
        <line x1={146} y1={136} x2={154} y2={142} />
        <line x1={116} y1={146} x2={116} y2={156} />
      </g>
      <ellipse cx={116} cy={241} rx={52} ry={5} fill="#6FA82C" opacity={0}>
        {!reduced && <animate attributeName="opacity" values="0;0;0.35;0.35;0.16;0.16;0.03;0.03;0;0" keyTimes={gT} dur={D} repeatCount="indefinite" />}
      </ellipse>

      {/* phone */}
      <rect x={212} y={150} width={66} height={128} rx={10} fill="#101010" />
      <rect x={218} y={160} width={54} height={108} rx={5} fill="#F7F8F4" />
      <circle cx={245} cy={167} r={2} fill="#101010" />
      <text x={245} y={192} textAnchor="middle" fontFamily="monospace" fontSize={8} fill="#57614F" letterSpacing={1}>ZONE 01</text>
      {/* slider track + fill + knob */}
      <rect x={227} y={208} width={36} height={6} rx={3} fill="#DFE4E9" />
      <rect x={227} y={208} width={36} height={6} rx={3} fill="#6FA82C" opacity={0}>
        {!reduced && <animate attributeName="opacity" values={trackV} keyTimes={trackT} dur={D} repeatCount="indefinite" />}
      </rect>
      <circle cx={235} cy={211} r={6.5} fill="#FFFFFF" stroke="#7F8C8D" strokeWidth={0.9}>
        {!reduced && <animate attributeName="cx" values={knobV} keyTimes={knobT} dur={D} repeatCount="indefinite" />}
      </circle>
      {/* tap ripple */}
      <circle cx={245} cy={211} r={4} stroke="#6FA82C" strokeWidth={1.4} fill="none" opacity={0}>
        {!reduced && <animate attributeName="r" values="4;4;26;26" keyTimes="0;0.1;0.18;1" dur={D} repeatCount="indefinite" />}
        {!reduced && <animate attributeName="opacity" values="0;0.9;0;0" keyTimes="0;0.1;0.18;1" dur={D} repeatCount="indefinite" />}
      </circle>
      {/* mode readout */}
      <text x={245} y={240} textAnchor="middle" fontFamily="monospace" fontSize={7.5} fill="#57614F">LIGHTS</text>
      <text x={245} y={252} textAnchor="middle" fontFamily="monospace" fontSize={8} fontWeight="bold" fill="#101010" opacity={1}>
        OFF
        {!reduced && <animate attributeName="opacity" values="1;1;0;0;1" keyTimes="0;0.12;0.16;0.94;1" dur={D} repeatCount="indefinite" />}
      </text>
      <text x={245} y={252} textAnchor="middle" fontFamily="monospace" fontSize={8} fontWeight="bold" fill="#4E8A1C" opacity={0}>
        100%
        {!reduced && <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0;0.2;0.24;0.38;0.44;1" dur={D} repeatCount="indefinite" />}
      </text>
      <text x={245} y={252} textAnchor="middle" fontFamily="monospace" fontSize={8} fontWeight="bold" fill="#4E8A1C" opacity={0}>
        50%
        {!reduced && <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0;0.48;0.52;0.62;0.68;1" dur={D} repeatCount="indefinite" />}
      </text>
      <text x={245} y={252} textAnchor="middle" fontFamily="monospace" fontSize={8} fontWeight="bold" fill="#4E8A1C" opacity={0}>
        AUTO
        {!reduced && <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0;0.76;0.8;0.88;0.93;1" dur={D} repeatCount="indefinite" />}
      </text>

      {/* signal phone → bulb */}
      <path id="tap-signal" d="M 232 150 C 210 96, 170 84, 136 100" stroke="#6FA82C" strokeWidth={1} strokeDasharray="2 4" opacity={0.55} fill="none" />
      {!reduced && [0, 0.012, 0.024].map((off, i) => {
        const t0 = 0.1 + off, t1 = 0.2 + off;
        return (
          <circle key={i} r={2.6} fill="#6FA82C" opacity={0}>
            <animateMotion dur={D} repeatCount="indefinite" calcMode="linear" keyPoints="0;0;1;1" keyTimes={`0;${t0};${t1};1`}>
              <mpath xlinkHref="#tap-signal" />
            </animateMotion>
            <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes={`0;${t0};${t0 + 0.008};${t1 - 0.008};${t1};1`} dur={D} repeatCount="indefinite" />
          </circle>
        );
      })}
      <circle cx={116} cy={120} r={22} stroke="#6FA82C" strokeWidth={1.2} fill="none" opacity={0}>
        {!reduced && <animate attributeName="r" values="18;18;30;30" keyTimes="0;0.2;0.26;1" dur={D} repeatCount="indefinite" />}
        {!reduced && <animate attributeName="opacity" values="0;0;0.8;0;0" keyTimes="0;0.2;0.23;0.3;1" dur={D} repeatCount="indefinite" />}
      </circle>
    </svg>
  );
};

// Living mesh network — nodes with data packets traveling along relay paths.
const MESH_NODES = [
  [150, 44], [70, 92], [230, 92], [150, 132],
  [42, 178], [150, 218], [258, 178],
  [78, 262], [222, 262], [150, 316],
];
const MESH_EDGES = [
  [0, 1], [0, 2], [1, 3], [2, 3], [1, 4], [2, 6],
  [3, 5], [4, 5], [6, 5], [4, 7], [6, 8], [5, 7], [5, 8], [7, 9], [8, 9],
];
const PACKET_PATHS = [
  [0, 1, 3, 5, 9],
  [2, 6, 8, 9],
  [0, 2, 3, 5, 4, 7],
];

export const MeshPulse = ({ className = "" }) => {
  const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const pathFor = (seq) => seq.map((n, i) => `${i === 0 ? "M" : "L"}${MESH_NODES[n][0]} ${MESH_NODES[n][1]}`).join(" ");
  return (
    <svg viewBox="0 0 300 350" fill="none" className={className} aria-hidden="true">
      {MESH_EDGES.map(([a, b], i) => (
        <motion.line
          key={i}
          x1={MESH_NODES[a][0]} y1={MESH_NODES[a][1]} x2={MESH_NODES[b][0]} y2={MESH_NODES[b][1]}
          stroke="#BDC3C7" strokeWidth="1"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.06, duration: 0.5 }}
        />
      ))}
      {MESH_NODES.map(([x, y], i) => (
        <g key={i}>
          <motion.rect
            x={x - 5} y={y - 5} width={10} height={10}
            style={{ fill: "var(--paper)", transformOrigin: `${x}px ${y}px`, transformBox: "fill-box" }}
            stroke={i === 3 ? "#6FA82C" : "#7F8C8D"} strokeWidth={i === 3 ? 1.6 : 1}
            initial={{ scale: 0, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 + i * 0.07, type: "spring", stiffness: 260, damping: 16 }}
          />
          {i === 3 && (
            <>
              <circle cx={x} cy={y} r={14} stroke="#6FA82C" strokeOpacity={0.5} fill="none">
                {!reduced && <animate attributeName="r" values="10;22;10" dur="3s" repeatCount="indefinite" />}
                {!reduced && <animate attributeName="stroke-opacity" values="0.6;0.05;0.6" dur="3s" repeatCount="indefinite" />}
              </circle>
              <circle cx={x} cy={y} r={3} fill="#6FA82C" />
            </>
          )}
        </g>
      ))}
      {!reduced &&
        PACKET_PATHS.map((seq, i) => (
          <g key={`p${i}`}>
            <path d={pathFor(seq)} stroke="none" fill="none" id={`mesh-path-${i}`} />
            <circle r={3.2} fill={i === 1 ? "#101010" : "#6FA82C"}>
              <animateMotion dur={`${4.5 + i * 1.3}s`} repeatCount="indefinite" begin={`${i * 1.5}s`}>
                <mpath xlinkHref={`#mesh-path-${i}`} />
              </animateMotion>
            </circle>
            <circle r={7} fill={i === 1 ? "#101010" : "#6FA82C"} opacity={0.18}>
              <animateMotion dur={`${4.5 + i * 1.3}s`} repeatCount="indefinite" begin={`${i * 1.5}s`}>
                <mpath xlinkHref={`#mesh-path-${i}`} />
              </animateMotion>
            </circle>
          </g>
        ))}
    </svg>
  );
};

// Sensor with radiating coverage cone — capabilities / applications accent.
export const SensorCone = ({ className = "" }) => (
  <svg viewBox="0 0 300 260" fill="none" className={className} aria-hidden="true">
    <motion.rect x={120} y={18} width={60} height={22} rx={3} stroke="#7F8C8D" strokeWidth="1.4" {...draw(0)} />
    <motion.circle cx={150} cy={52} r={14} stroke="#6FA82C" strokeWidth="1.4" {...draw(0.2)} />
    <circle cx={150} cy={52} r={5} fill="#6FA82C" className="animate-pulse-dot" />
    {[70, 110, 150].map((r, i) => (
      <motion.path
        key={r}
        d={`M ${150 - r} 220 A ${r} ${r} 0 0 1 ${150 + r} 220`}
        stroke={i === 1 ? "#101010" : "#BDC3C7"} strokeWidth="1" strokeDasharray="4 5"
        {...draw(0.4 + i * 0.15)}
      />
    ))}
    <motion.path d="M150 66 L45 220 M150 66 L255 220 M150 66 V220" stroke="#7F8C8D" strokeWidth="1" strokeDasharray="3 5" {...draw(0.6)} />
    <motion.path d="M45 220 H255" stroke="#BDC3C7" strokeWidth="1.2" {...draw(0.8)} />
    <Node cx={45} cy={220} delay={0.2} />
    <Node cx={150} cy={220} delay={0.4} color="#101010" />
    <Node cx={255} cy={220} delay={0.6} />
  </svg>
);
